import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindow, OverlayView } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import apiClientconops from "../../../ApiEndpoint/apiClientconops";
import apiUrlconops from "../../../ApiEndpoint/configconops";
import { ConopsContext } from "../../conops-hooks/ConopsContext";
import { Button, Slider } from 'antd';

const mapContainerStyle = {
  width: "100%",
  height: "450px",
  borderRadius: "10px",
};
const defaultCenter = { lat: 39.5, lng: -98.35 }; // Center of USA
const defaultZoom = 4.3;

const FacilityMap = () => {
  const [facilities, setFacilities] = useState([]);
  const [nearbyVeoliaFacilities, setNearbyVeoliaFacilities] = useState([]);
  const [selectedMarkerPosition, setSelectedMarkerPosition] = useState(null);
  const [currentClickedRegistryId, setCurrentClickedRegistryId] = useState(null);
  const [currentClickedFacility, setCurrentClickedFacility] = useState(null);
  const [radiusMiles, setRadiusMiles] = useState(0);
  const [selectedVeoliaFacility, setSelectedVeoliaFacility] = useState(null);
  const [map, setMap] = useState(null);
  const [clusterer, setClusterer] = useState(null);
  const [nearbyClusterer, setNearbyClusterer] = useState(null);
  const debounceTimerRef = useRef(null);
  const fetchControllerRef = useRef(null);
  const markersMapRef = useRef(new Map());
  const radiusCircleRef = useRef(null);
  const markerRegistryIdRef = useRef(null);

  const { selectedOpenSearchValue, selectedState, selectedCounty, selectedCity, selectedFacility, setRegisterId,
    setFacilityTableObject, setMarkerRegistryId, selectedFacilityType, setSelectedFacilityType, setCountyObject, refineSearchProjectObject, markerRegistryId } = useContext(ConopsContext);

  // ✅ Keep ref in sync with markerRegistryId
  useEffect(() => {
    markerRegistryIdRef.current = markerRegistryId;
  }, [markerRegistryId]);

  const handleClick = (type) => {
    setSelectedFacilityType(type);
    setRegisterId(null);
    clearVeoliaMarkersAndCircle();
  };

  // ✅ Helper function to clear Veolia markers and circle
  const clearVeoliaMarkersAndCircle = () => {
    setNearbyVeoliaFacilities([]);
    setCurrentClickedRegistryId(null);
    setCurrentClickedFacility(null);
    setRadiusMiles(0);
    setSelectedVeoliaFacility(null);
    setSelectedMarkerPosition(null);

    // ✅ Cancel any pending fetch requests
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
      fetchControllerRef.current = null;
    }

    // ✅ Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // ✅ Remove radius circle
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }

    if (nearbyClusterer) {
      nearbyClusterer.clearMarkers();
      nearbyClusterer.setMap(null);
    }
  };

  // ✅ Reset radius slider when search filters change
  useEffect(() => {
    setRadiusMiles(0);
  }, [markerRegistryId, selectedOpenSearchValue, selectedState, selectedCounty, selectedCity, selectedFacility, selectedFacilityType, refineSearchProjectObject]);

  // ✅ Load Google Maps script
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBUzGu9ytbWBaOeO2peYl5qhZhmp9gPaQY",
    libraries: ["marker"],
  });

  // ✅ UPDATED: Fetch facility data - clear selection when facilities change
  useEffect(() => {
    const controller = new AbortController();

    const fetchFacilities = async () => {
      try {
        if (map) {
          map.setZoom(4);
          map.panTo({ lat: 39.8283, lng: -98.5795 });
        }

        if (
          !selectedOpenSearchValue &&
          !selectedState &&
          !selectedCounty &&
          !selectedCity &&
          !selectedFacility &&
          !selectedFacilityType &&
          !refineSearchProjectObject
        ) {
          setFacilities([]);
          if (clusterer) clusterer.clearMarkers();
          clearVeoliaMarkersAndCircle();
          return;
        }

        const params = {};

        if (selectedOpenSearchValue) params.q = selectedOpenSearchValue;
        if (selectedState) params.fac_state = selectedState;
        if (selectedCounty) params.fac_county = selectedCounty;
        if (selectedCity) params.fac_city = selectedCity;
        if (selectedFacilityType) params.fac_type = selectedFacilityType;

        const registryIdFinal = selectedFacility;
        if (registryIdFinal) params.registry_id = registryIdFinal;

        if (refineSearchProjectObject) {
          Object.entries(refineSearchProjectObject).forEach(([key, value]) => {
            const isEmpty =
              value === null ||
              value === "" ||
              value === undefined ||
              (typeof value === "string" && value.trim() === "");

            if (!isEmpty) {
              params[key] = value;
            }
          });
        }

        if (clusterer) clusterer.clearMarkers();

        // ✅ NEW: Clear selection when fetching new facilities
        clearVeoliaMarkersAndCircle();

        const response = await apiClientconops.get(`${apiUrlconops}/get_facility_markers`, {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        if (response?.data?.data) {
          const data = response.data.data;
          setFacilities(data);

          if (data.length > 0 && map) {
            const validMarkers = data.filter(
              (f) =>
                !isNaN(parseFloat(f.LATITUDE_MEASURE)) &&
                !isNaN(parseFloat(f.LONGITUDE_MEASURE))
            );

            if (validMarkers.length > 0) {
              const avgLat =
                validMarkers.reduce((sum, f) => sum + parseFloat(f.LATITUDE_MEASURE), 0) /
                validMarkers.length;
              const avgLng =
                validMarkers.reduce((sum, f) => sum + parseFloat(f.LONGITUDE_MEASURE), 0) /
                validMarkers.length;
              map.panTo({ lat: avgLat, lng: avgLng });
            }
          } else {
            setFacilities([]);
          }
        } else {
          console.warn("Unexpected API response:", response);
          setFacilities([]);
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        console.error("Error fetching facility data:", error);
        setFacilities([]);
      }
    };

    fetchFacilities();
    setMarkerRegistryId(null);

    return () => {
      controller.abort();
    };
  }, [selectedOpenSearchValue, selectedState, selectedCounty, selectedCity, selectedFacility, selectedFacilityType, refineSearchProjectObject]);

  // ✅ Initialize map
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // ✅ Function to fetch nearby Veolia facilities with proper cancellation
  const fetchNearbyVeoliaFacilities = useCallback(async (facility, radius) => {
    // ✅ Cancel previous fetch if it exists
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }

    // ✅ Create new controller for this fetch
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    try {
      const targetRegistryId = facility?.REGISTRY_ID;
      const facilityLat = parseFloat(facility.LATITUDE_MEASURE);
      const facilityLng = parseFloat(facility.LONGITUDE_MEASURE);

      // ✅ Remove old RADIUS circle first
      if (radiusCircleRef.current) {
        radiusCircleRef.current.setMap(null);
        radiusCircleRef.current = null;
      }

      // ✅ If radius is 0, clear markers and return (NO CIRCLE)
      if (radius === 0 || radius === null) {
        setNearbyVeoliaFacilities([]);
        return;
      }

      // ✅ Draw NEW radius circle only if radius > 0
      if (!isNaN(facilityLat) && !isNaN(facilityLng) && map) {
        const circle = new window.google.maps.Circle({
          strokeColor: "#0066FF",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#0066FF",
          fillOpacity: 0.15,
          map: map,
          center: { lat: facilityLat, lng: facilityLng },
          radius: radius * 1609.34,
          clickable: false,
          zIndex: 998,
        });
        radiusCircleRef.current = circle;
      }

      // ✅ Fetch nearby Veolia facilities
      const nearbyResponse = await apiClientconops.get(`${apiUrlconops}/get_nearby_facilities`, {
        params: {
          registry_id: targetRegistryId,
          radius_miles: radius
        },
        signal: controller.signal,
      });

      // ✅ Check if aborted before updating state
      if (controller.signal.aborted) return;

      if (nearbyResponse?.data?.success && nearbyResponse?.data?.data) {
        setNearbyVeoliaFacilities(nearbyResponse.data.data);
        console.log(`Found ${nearbyResponse.data.count} nearby Veolia facilities within ${radius} miles`);
      } else {
        setNearbyVeoliaFacilities([]);
      }

    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('Fetch cancelled');
        return;
      }
      console.error("Error fetching nearby facilities:", error);
      setNearbyVeoliaFacilities([]);
    } finally {
      // ✅ Clear controller reference if this was the active one
      if (fetchControllerRef.current === controller) {
        fetchControllerRef.current = null;
      }
    }
  }, [map]);

  // ✅ Handle marker click
  const handleMarkerClick = async (facility, marker) => {
    const controller = new AbortController();

    try {
      const targetRegistryId = facility?.REGISTRY_ID;

      // ✅ If clicking the same facility, keep circle and nearby facilities
      if (markerRegistryIdRef.current === targetRegistryId) {
        // If circle doesn't exist but radiusMiles > 0, redraw it
        if (radiusMiles > 0 && !radiusCircleRef.current) {
          fetchNearbyVeoliaFacilities(facility, radiusMiles);
        }
        return;
      }

      // ✅ Cancel any pending fetch requests when selecting new marker
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
        fetchControllerRef.current = null;
      }

      // ✅ Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      const facilityLat = parseFloat(facility.LATITUDE_MEASURE);
      const facilityLng = parseFloat(facility.LONGITUDE_MEASURE);

      // ✅ Set marker position for label
      if (!isNaN(facilityLat) && !isNaN(facilityLng)) {
        setSelectedMarkerPosition({ lat: facilityLat, lng: facilityLng });
      }

      setNearbyVeoliaFacilities([]);
      setSelectedVeoliaFacility(null);

      // ✅ Remove radius circle (preserve radiusMiles value)
      if (radiusCircleRef.current) {
        radiusCircleRef.current.setMap(null);
        radiusCircleRef.current = null;
      }

      const response = await apiClientconops.get(`${apiUrlconops}/search_facilities`, {
        params: { registry_id: targetRegistryId },
        signal: controller.signal,
      });
      const facilityObj = response?.data?.data ? response.data.data[0] : null;
      setRegisterId(targetRegistryId);
      setMarkerRegistryId(targetRegistryId);
      setCurrentClickedRegistryId(targetRegistryId);
      setCurrentClickedFacility(facility);
      setFacilityTableObject([facilityObj]);
      setCountyObject({ county: facility.FAC_COUNTY, state: facility?.FAC_STATE })

      // ✅ If radius > 0, fetch nearby facilities for the new marker
      if (radiusMiles > 0) {
        fetchNearbyVeoliaFacilities(facility, radiusMiles);
      }

    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error("Error fetching facility info:", error);
      setNearbyVeoliaFacilities([]);
    }
  };

  // ✅ Debounced radius change handler
  const handleRadiusChange = (value) => {
    setRadiusMiles(value);

    // ✅ Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // ✅ Cancel any pending fetch
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
      fetchControllerRef.current = null;
    }

    // ✅ Set new timer - API call after 1 second of inactivity
    debounceTimerRef.current = setTimeout(() => {
      if (currentClickedFacility && currentClickedRegistryId) {
        fetchNearbyVeoliaFacilities(currentClickedFacility, value);
      }
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
      }
    };
  }, []);

  // ✅ Create clusterer
  useEffect(() => {
    if (!map || !isLoaded) return;

    if (clusterer) {
      clusterer.clearMarkers();
      clusterer.setMap(null);
    }

    if (facilities.length === 0) {
      setClusterer(null);
      markersMapRef.current.clear();
      return;
    }

    const googleMarkers = facilities
      .map((f) => {
        const lat = parseFloat(f.LATITUDE_MEASURE);
        const lng = parseFloat(f.LONGITUDE_MEASURE);
        if (isNaN(lat) || isNaN(lng)) return null;

        let iconUrl = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
        if (f.FAC_TYPE === "WTP") {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
        } else if (f.FAC_TYPE === "WWTP") {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
        } else if (f.FAC_TYPE === "WTP_WWTP") {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
        }

        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          title: f.FAC_NAME,
          icon: {
            url: iconUrl,
            size: new window.google.maps.Size(32, 32),
            scaledSize: new window.google.maps.Size(28, 28),
            anchor: new window.google.maps.Point(14, 28),
          },
          optimized: true,
        });

        marker.facility = f;
        markersMapRef.current.set(f.REGISTRY_ID, marker);
        marker.addListener("click", () => handleMarkerClick(f, marker));
        return marker;
      })
      .filter(Boolean);

    const newClusterer = new MarkerClusterer({ map, markers: googleMarkers });
    setClusterer(newClusterer);

    return () => {
      googleMarkers.forEach(marker => {
        window.google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
      if (newClusterer) {
        newClusterer.clearMarkers();
        newClusterer.setMap(null);
      }
      markersMapRef.current.clear();
    };
  }, [map, facilities, isLoaded]);

  useEffect(() => {
    if (!currentClickedRegistryId) {
      clearVeoliaMarkersAndCircle();
    }
  }, [currentClickedRegistryId]);

  // ✅ Create clusterer for nearby Veolia facilities
  useEffect(() => {
    if (!map || !isLoaded) return;

    if (nearbyClusterer) {
      nearbyClusterer.clearMarkers();
      nearbyClusterer.setMap(null);
    }

    if (nearbyVeoliaFacilities.length === 0) {
      setNearbyClusterer(null);
      return;
    }

    const nearbyMarkers = nearbyVeoliaFacilities
      .map((f) => {
        const lat = parseFloat(f.LATITUDE_MEASURE);
        const lng = parseFloat(f.LONGITUDE_MEASURE);
        if (isNaN(lat) || isNaN(lng)) return null;

        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          title: `${f.FAC_NAME} (Veolia - ${f.distance_miles?.toFixed(2)} mi)`,
          icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
            size: new window.google.maps.Size(32, 32),
            scaledSize: new window.google.maps.Size(28, 28),
            anchor: new window.google.maps.Point(14, 28),
          },
          optimized: true,
          zIndex: 1000,
        });

        marker.addListener("click", () => {
          setSelectedVeoliaFacility(f);
        });

        return marker;
      })
      .filter(Boolean);

    const newNearbyClusterer = new MarkerClusterer({
      map,
      markers: nearbyMarkers,
    });
    setNearbyClusterer(newNearbyClusterer);

    return () => {
      nearbyMarkers.forEach(marker => {
        window.google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
      if (newNearbyClusterer) {
        newNearbyClusterer.clearMarkers();
        newNearbyClusterer.setMap(null);
      }
    };
  }, [map, nearbyVeoliaFacilities, isLoaded]);

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <div className="card p-3">
      <div className="row pb-2">
        <div className="col-1">
          <Button
            type={selectedFacilityType === "wtp" ? "primary" : "default"}
            size="small"
            onClick={() => handleClick("wtp")}
            className={`w-100 ${refineSearchProjectObject ? "hidden" : ""}`}
          >
            WTP
          </Button>
        </div>

        <div className="col-1">
          <Button
            type={selectedFacilityType === "wwtp" ? "primary" : "default"}
            size="small"
            onClick={() => handleClick("wwtp")}
            className={`w-100 ${refineSearchProjectObject ? "hidden" : ""}`}
          >
            WWTP
          </Button>
        </div>

        <div className="col-1">
          <Button
            type={selectedFacilityType === "wtp_wwtp" ? "primary" : "default"}
            size="small"
            onClick={() => handleClick("wtp_wwtp")}
            className={`w-100 ${refineSearchProjectObject ? "hidden" : ""}`}
          >
            BOTH
          </Button>
        </div>

        <div className={currentClickedRegistryId ? "col-5" : "col-9"}></div>

        {currentClickedRegistryId && (
          <div className="col-4">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "12px", whiteSpace: "nowrap", minWidth: "45px" }}>
                VNA Facility Within:
              </span>
              <Slider
                min={0}
                max={200}
                value={radiusMiles}
                onChange={handleRadiusChange}
                style={{ flex: 1 }}
                tooltip={{ formatter: (value) => `${value} mi` }}
              />
              <span style={{ fontSize: "12px", fontWeight: "bold", minWidth: "60px" }}>
                {radiusMiles} mi
              </span>
            </div>
          </div>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={defaultZoom}
          mapTypeId="roadmap"
          onLoad={onLoad}
        >
          {/* ✅ Selected marker label */}
          {selectedMarkerPosition && (
            <OverlayView
              position={selectedMarkerPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  position: 'absolute',
                  transform: 'translate(-50%, -100%)',
                  marginTop: '-35px',
                  background: '#FFD700',
                  color: '#000',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  whiteSpace: 'nowrap',
                  border: '2px solid #FFA500',
                }}
              >
                ⭐ SELECTED
              </div>
            </OverlayView>
          )}

          {selectedVeoliaFacility && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedVeoliaFacility.LATITUDE_MEASURE),
                lng: parseFloat(selectedVeoliaFacility.LONGITUDE_MEASURE),
              }}
              onCloseClick={() => setSelectedVeoliaFacility(null)}
            >
              <div style={{ padding: "8px", maxWidth: "250px" }}>
                <h6 style={{ margin: "0 0 8px 0", color: "#0066FF", fontWeight: "bold" }}>
                  {selectedVeoliaFacility.FAC_NAME}
                </h6>
                <div style={{ fontSize: "12px", lineHeight: "1.6" }}>
                  <div><strong>Type:</strong> {selectedVeoliaFacility.FAC_TYPE || "N/A"}</div>
                  <div><strong>Distance:</strong> {selectedVeoliaFacility.distance_miles?.toFixed(2)} miles</div>
                  <div><strong>City:</strong> {selectedVeoliaFacility.FAC_CITY || "N/A"}</div>
                  <div><strong>County:</strong> {selectedVeoliaFacility.FAC_COUNTY || "N/A"}</div>
                  <div><strong>State:</strong> {selectedVeoliaFacility.FAC_STATE || "N/A"}</div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        <div
          style={{
            position: "absolute",
            bottom: "4px",
            left: "70px",
            background: "rgba(255,255,255,0.85)",
            padding: "6px 10px",
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <img src="https://maps.google.com/mapfiles/ms/icons/green-dot.png" alt="WTP" style={{ width: 12, height: 12 }} />
            <span>WTP</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <img src="https://maps.google.com/mapfiles/ms/icons/yellow-dot.png" alt="WWTP" style={{ width: 12, height: 12 }} />
            <span>WWTP</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <img src="https://maps.google.com/mapfiles/ms/icons/red-dot.png" alt="WTP_WWTP" style={{ width: 12, height: 12 }} />
            <span>WTP & WWTP</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <img src="https://maps.google.com/mapfiles/ms/icons/orange-dot.png" alt="Veolia" style={{ width: 12, height: 12 }} />
            <span>Veolia (Nearby)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityMap;